// Columns dropdown for the accounts table. Each row is a drag-sortable item
// with a visibility checkbox. Drag handle uses @dnd-kit/sortable.

import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Settings2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

// Minimal column shape required by the menu — any per-feature ColumnDef that
// has `{ key, label, noFilter? }` is compatible (Accounts, SensorsTab, etc.).
export interface MenuColumn {
  key: string;
  label?: string;
  noFilter?: boolean;
}

interface SortableRowProps {
  column: MenuColumn;
  checked: boolean;
  onToggle: () => void;
}

function SortableRow({ column, checked, onToggle }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm select-none',
        isDragging ? 'bg-accent/50 shadow-sm' : 'hover:bg-accent/30',
      )}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
        aria-label={`Drag ${column.label || column.key} to reorder`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <label className="flex flex-1 items-center gap-2 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={onToggle} />
        <span className={cn(!column.label && 'text-muted-foreground italic')}>
          {column.label || '(no label)'}
        </span>
      </label>
    </div>
  );
}

export interface AccountsColumnsMenuProps {
  orderedColumns: MenuColumn[];
  isVisible: (key: string) => boolean;
  onToggle: (key: string) => void;
  onReorder: (nextOrder: string[]) => void;
}

export function AccountsColumnsMenu({
  orderedColumns,
  isVisible,
  onToggle,
  onReorder,
}: AccountsColumnsMenuProps) {
  const [open, setOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Hide the "actions" pseudo-column from the reorder list — it always sits last.
  const reorderable = orderedColumns.filter((c) => !c.noFilter);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedColumns.map((c) => c.key);
    const fromIdx = ids.indexOf(String(active.id));
    const toIdx = ids.indexOf(String(over.id));
    if (fromIdx === -1 || toIdx === -1) return;
    onReorder(arrayMove(ids, fromIdx, toIdx));
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings2 className="h-4 w-4 mr-1" /> Columns
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-0">
        <DropdownMenuLabel className="px-2 py-2">
          Toggle and reorder columns
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-[60vh] overflow-y-auto p-1">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={reorderable.map((c) => c.key)}
              strategy={verticalListSortingStrategy}
            >
              {reorderable.map((col) => (
                <SortableRow
                  key={col.key}
                  column={col}
                  checked={isVisible(col.key)}
                  onToggle={() => onToggle(col.key)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
